/**
 * Zustand store for FAQ data
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const FAQ_KEY = "khays_faqs";

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  order: number;
  enabled: boolean;
}

function loadFromStorage(): FaqItem[] {
  try { const s = localStorage.getItem(FAQ_KEY); if (s) return JSON.parse(s); } catch {} return [];
}

interface FaqState {
  faqs: FaqItem[];
  _rev: number;
  setFaqs: (faqs: FaqItem[]) => void;
  reload: () => void;
}

export const useFaqStore = create<FaqState>()(
  devtools(
    (set, get) => ({
      faqs: loadFromStorage(),
      _rev: 0,
      setFaqs: (faqs) => {
        localStorage.setItem(FAQ_KEY, JSON.stringify(faqs));
        set({ faqs, _rev: get()._rev + 1 });
      },
      reload: () => set({ faqs: loadFromStorage(), _rev: get()._rev + 1 }),
    }),
    { name: "FaqStore" }
  )
);

function save(faqs: FaqItem[]): void { useFaqStore.getState().setFaqs(faqs); }
function load(): FaqItem[] { return useFaqStore.getState().faqs; }

export function getFaqs(): FaqItem[] { return [...load()].sort((a, b) => a.order - b.order); }
export function getEnabledFaqs(): FaqItem[] { return getFaqs().filter(f => f.enabled); }

export function addFaq(question: string, answer: string = ""): FaqItem {
  const faqs = load();
  const item: FaqItem = {
    id: `faq_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    question, answer, order: faqs.length, enabled: true,
  };
  save([...faqs, item]);
  return item;
}

export function updateFaq(id: string, updates: Partial<Pick<FaqItem, "question" | "answer" | "enabled">>): void {
  const faqs = load().map(f => f.id === id ? { ...f, ...updates } : f);
  save(faqs);
}

export function removeFaq(id: string): void {
  const faqs = load().filter(f => f.id !== id).map((f, i) => ({ ...f, order: i }));
  save(faqs);
}

export function reorderFaqs(ids: string[]): void {
  const faqs = load();
  const map = new Map(faqs.map(f => [f.id, f]));
  const reordered = ids.map((id, i) => {
    const item = map.get(id);
    if (item) return { ...item, order: i };
    return null;
  }).filter(Boolean) as FaqItem[];
  save(reordered);
}
